import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Plus, Trash2, UserX } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate, fmtDateTime } from "@/lib/api";
import { DemoBadge, ErrorBox, Field, PageHead, Spinner, StatusBadge, useFetch } from "@/components/common";

const EMPTY_RX = { medication: "", dosage: "", frequency: "", duration: "", notes: "" };

function RxDraft({ items, onChange }) {
  const upd = (i, k, v) => onChange(items.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  return (
    <div className="relative overflow-hidden rounded-2xl border-2 border-dashed border-amber-300 bg-amber-50/40 p-4" data-testid="rx-draft">
      <span className="pointer-events-none absolute right-3 top-3 rounded bg-amber-400 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-amber-950">Draft only — not issued</span>
      <p className="mb-3 text-sm font-bold">Prescription review (draft)</p>
      <p className="mb-3 text-[11px] text-amber-900">Prescription issuance is disabled in Phase 1. Only the authorized physician may make clinical decisions; nothing is auto-approved.</p>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="grid grid-cols-2 gap-2 rounded-xl bg-white p-2 sm:grid-cols-6">
            <input className="field col-span-2" placeholder="Medication" value={it.medication} onChange={(e) => upd(i, "medication", e.target.value)} data-testid={`rx-med-${i}`} />
            <input className="field" placeholder="Dosage" value={it.dosage} onChange={(e) => upd(i, "dosage", e.target.value)} />
            <input className="field" placeholder="Frequency" value={it.frequency} onChange={(e) => upd(i, "frequency", e.target.value)} />
            <input className="field" placeholder="Duration" value={it.duration} onChange={(e) => upd(i, "duration", e.target.value)} />
            <button type="button" className="btn-ghost text-red-600" onClick={() => onChange(items.filter((_, j) => j !== i))}><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
      <button type="button" className="btn-secondary btn-sm mt-3" onClick={() => onChange([...items, { ...EMPTY_RX }])} data-testid="rx-add-item"><Plus size={13} /> Add item</button>
    </div>
  );
}

export default function ProviderRequest() {
  const { id } = useParams();
  const { data: sr, error, loading, reload } = useFetch(`/provider/requests/${id}`);
  const [rec, setRec] = useState(null);
  useEffect(() => {
    if (sr) {
      const r = sr.record || {};
      setRec({ chief_complaint: r.chief_complaint || "", assessment: r.assessment || "", plan: r.plan || "", notes: r.notes || "",
        prescription_draft: r.prescription_draft || [], physician_reviewed: r.prescription_status === "REVIEWED_BY_PHYSICIAN_NOT_ISSUED" });
    }
  }, [sr]);
  const call = async (fn, msg) => {
    try { await fn(); toast.success(msg); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const save = () => call(() => api.put(`/provider/requests/${id}/record`, { ...rec, prescription_draft: rec.prescription_draft.filter((x) => x.medication) }), "Consultation record saved");
  if (loading && !sr) return <Spinner />;
  if (!sr) return <ErrorBox error={error} />;
  const editable = ["CONFIRMED", "COMPLETED"].includes(sr.status);
  const set = (k) => (e) => setRec({ ...rec, [k]: e.target.value });
  return (
    <div className="space-y-6">
      <Link to="/provider" className="btn-ghost btn-sm -ml-2" data-testid="provider-back"><ArrowLeft size={14} /> Queue</Link>
      <PageHead eyebrow={sr.benefit_name} title={sr.patient.name} sub={fmtDateTime(sr.scheduled_at)}
        actions={<><StatusBadge status={sr.status} testId="provider-request-status" />{sr.is_demo && <DemoBadge />}</>} />
      <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
        <aside className="surface h-fit space-y-3 p-5 text-sm" data-testid="patient-info">
          <p className="label">Patient (authorized for this request)</p>
          <p><span className="text-slate-500">Relationship:</span> <b>{sr.patient.relationship}</b></p>
          <p><span className="text-slate-500">Birthdate:</span> <b>{fmtDate(sr.patient.birthdate)}</b></p>
          <p><span className="text-slate-500">Account holder:</span> <b>{sr.patient.account_holder}</b></p>
          <p><span className="text-slate-500">Contact:</span> <b>{sr.patient.contact}</b></p>
          <p><span className="text-slate-500">Consent on file:</span> <b>{sr.patient.consent_on_file ? "Yes" : "No"}</b></p>
          {sr.reason && <div className="rounded-xl bg-slate-50 p-3 text-xs"><p className="label">Reason</p>{sr.reason}</div>}
          {sr.past_records?.length > 0 && <div><p className="label">Previous consultations</p>{sr.past_records.map((p) => <p key={p.created_at} className="text-xs">{fmtDate(p.created_at)} · {p.assessment?.slice(0, 60)}</p>)}</div>}
          <div className="flex flex-col gap-2 pt-2">
            {sr.status === "REQUESTED" && <button className="btn-primary" onClick={() => call(() => api.post(`/provider/requests/${id}/confirm`), "Confirmed")} data-testid="provider-confirm-button">Confirm appointment</button>}
            {sr.status === "CONFIRMED" && (
              <>
                <button className="btn-primary" onClick={() => window.confirm("Mark service completed? One benefit will be deducted.") && call(() => api.post(`/provider/requests/${id}/complete`), "Service completed — benefit deducted")} data-testid="provider-complete-button"><CheckCircle2 size={15} /> Complete service</button>
                <button className="btn-secondary" onClick={() => call(() => api.post(`/provider/requests/${id}/no-show`), "Marked as no-show")} data-testid="provider-noshow-button"><UserX size={15} /> No-show</button>
              </>
            )}
          </div>
        </aside>
        {rec && (
          <section className="surface space-y-4 p-6" data-testid="consultation-record-form">
            <h2 className="text-base font-bold">Consultation record & clinical assessment</h2>
            {!editable && <p className="text-xs text-slate-500">Confirm the appointment to start the record.</p>}
            <fieldset disabled={!editable} className="space-y-4">
              <Field label="Chief complaint"><textarea className="field" rows={2} value={rec.chief_complaint} onChange={set("chief_complaint")} data-testid="record-complaint-input" /></Field>
              <Field label="Clinical assessment"><textarea className="field" rows={3} value={rec.assessment} onChange={set("assessment")} data-testid="record-assessment-input" /></Field>
              <Field label="Plan"><textarea className="field" rows={2} value={rec.plan} onChange={set("plan")} data-testid="record-plan-input" /></Field>
              <Field label="Notes"><textarea className="field" rows={2} value={rec.notes} onChange={set("notes")} data-testid="record-notes-input" /></Field>
              <RxDraft items={rec.prescription_draft} onChange={(v) => setRec({ ...rec, prescription_draft: v })} />
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={rec.physician_reviewed} onChange={(e) => setRec({ ...rec, physician_reviewed: e.target.checked })} data-testid="record-reviewed-check" /> I am the authorized physician and have reviewed this draft (still not issued)</label>
              <button type="button" className="btn-primary" onClick={save} data-testid="record-save-button">Save record</button>
            </fieldset>
            {sr.record?.prescription_status && <p className="text-xs">Prescription status: <StatusBadge status={sr.record.prescription_status} /></p>}
          </section>
        )}
      </div>
    </div>
  );
}
